/**
 * Tax parameters for a given year, loaded from JSON.
 * Based on §32a EStG (German income tax formula).
 */
export interface IncomeTaxParams {
  /** Grundfreibetrag – income below this is tax-free (Zone 1) */
  grundfreibetrag: number;
  /** End of Zone 2 (progressive 14%–24%) */
  zone2End: number;
  /** End of Zone 3 (progressive 24%–42%) */
  zone3End: number;
  /** End of Zone 4 (42% flat, Spitzensteuersatz) */
  zone4End: number;
  /** Zone 2 formula coefficient 1: (factor1 * y + factor2) * y */
  zone2Factor1: number;
  /** Zone 2 formula coefficient 2 */
  zone2Factor2: number;
  /** Zone 3 formula coefficient 1: (factor1 * z + factor2) * z + constant */
  zone3Factor1: number;
  /** Zone 3 formula coefficient 2 */
  zone3Factor2: number;
  /** Zone 3 formula constant */
  zone3Constant: number;
  /** Zone 4 rate (Spitzensteuersatz, 42%) */
  zone4Rate: number;
  /** Zone 4 subtract constant */
  zone4Subtract: number;
  /** Zone 5 rate (Reichensteuersatz, 45%) */
  zone5Rate: number;
  /** Zone 5 subtract constant */
  zone5Subtract: number;
}

export interface SolidaritaetszuschlagParams {
  /** Soli rate (5.5%) */
  rate: number;
  /** Freigrenze for Steuerklasse 1, 2, 4, 5, 6 (annual tax) */
  freigrenze: number;
  /** Freigrenze for Steuerklasse 3 (annual tax) */
  freigrenzeStk3: number;
  /** Milderungszone factor (11.9%) */
  milderungszoneFactor: number;
}

export interface SocialSecurityBranchParams {
  /** Employee contribution rate */
  employeeRate: number;
  /** Beitragsbemessungsgrenze (monthly) */
  bbgMonthly: number;
}

export interface PflegeversicherungParams extends SocialSecurityBranchParams {
  /** Extra surcharge for childless employees over 23 */
  childlessSurcharge: number;
  /** Discount per additional child (2nd–5th, under 25) */
  childDiscount: number;
  /** Maximum number of children eligible for discount */
  maxChildDiscountChildren: number;
}

export interface SocialSecurityParams {
  rentenversicherung: SocialSecurityBranchParams;
  arbeitslosenversicherung: SocialSecurityBranchParams;
  krankenversicherung: SocialSecurityBranchParams & {
    /** Default Zusatzbeitrag rate (average) */
    defaultAdditionalRate: number;
  };
  pflegeversicherung: PflegeversicherungParams;
}

export interface DeductionParams {
  /** Werbungskostenpauschale (annual) */
  werbungskostenpauschale: number;
  /** Sonderausgabenpauschale (annual) */
  sonderausgabenpauschale: number;
  /** Entlastungsbetrag für Alleinerziehende (Steuerklasse 2) */
  entlastungsbetragAlleinerziehende: number;
  /** Additional Entlastungsbetrag per child beyond the first */
  entlastungsbetragProWeiteresKind: number;
  /** Kinderfreibetrag amount per 1.0 count (for Soli/KiSt calculation) */
  kinderfreibetragAmount: number;
}

/**
 * Complete tax parameters for a year.
 */
export interface TaxParams {
  year: number;
  incomeTax: IncomeTaxParams;
  solidaritaetszuschlag: SolidaritaetszuschlagParams;
  socialSecurity: SocialSecurityParams;
  deductions: DeductionParams;
}

/**
 * Available year entry for the year selector.
 */
export interface BruttoNettoYear {
  year: number;
  name: string;
  dataFolder: string;
  info?: string;
}

/**
 * Bundesland with church tax rate and PV surcharge.
 */
export interface Bundesland {
  key: string;
  name: string;
  kirchensteuerRate: number;
  /**
   * PV-Arbeitnehmer-Mehrbeitrag: In Sachsen tragen Arbeitnehmer 0,5 % mehr
   * als in allen anderen Bundesländern (Buß- und Bettag-Regelung 1995).
   */
  pvMehrbeitrag: number;
}

export const BUNDESLAENDER: Bundesland[] = [
  { key: 'baden-wuerttemberg', name: 'Baden-Württemberg', kirchensteuerRate: 0.08, pvMehrbeitrag: 0 },
  { key: 'bayern', name: 'Bayern', kirchensteuerRate: 0.08, pvMehrbeitrag: 0 },
  { key: 'berlin', name: 'Berlin', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'brandenburg', name: 'Brandenburg', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'bremen', name: 'Bremen', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'hamburg', name: 'Hamburg', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'hessen', name: 'Hessen', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'mecklenburg-vorpommern', name: 'Mecklenburg-Vorpommern', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'niedersachsen', name: 'Niedersachsen', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'nordrhein-westfalen', name: 'Nordrhein-Westfalen', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'rheinland-pfalz', name: 'Rheinland-Pfalz', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'saarland', name: 'Saarland', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'sachsen', name: 'Sachsen', kirchensteuerRate: 0.09, pvMehrbeitrag: 0.005 },
  { key: 'sachsen-anhalt', name: 'Sachsen-Anhalt', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'schleswig-holstein', name: 'Schleswig-Holstein', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 },
  { key: 'thueringen', name: 'Thüringen', kirchensteuerRate: 0.09, pvMehrbeitrag: 0 }
];

export const STEUERKLASSEN = [1, 2, 3, 4, 5, 6] as const;
export type Steuerklasse = (typeof STEUERKLASSEN)[number];

export const KINDERFREIBETRAG_OPTIONS = [0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6];

export type KrankenversicherungsArt = 'gesetzlich' | 'freiwillig' | 'privat';

export const KRANKENVERSICHERUNGS_ARTEN: { key: KrankenversicherungsArt; label: string }[] = [
  { key: 'gesetzlich', label: 'Gesetzlich pflichtversichert' },
  { key: 'freiwillig', label: 'Freiwillig gesetzlich versichert' },
  { key: 'privat', label: 'Privat versichert' }
];
